import { useSyncExternalStore } from 'react';
import { markInstallPromptDone } from './installPromptSchedule';

/**
 * Module-level store for Chrome/Android's `beforeinstallprompt` event.
 *
 * The event fires once, early, and calling `.preventDefault()` on it is the
 * ONLY way to defer the native mini-infobar and re-trigger it later from our
 * own button — if nothing listens for it before our modal ever mounts, the
 * browser shows its own prompt (or the event is lost) and `promptInstall()`
 * below has nothing to call. So this module captures it at APP BOOT
 * (imported for its side effect from `src/index.js`), independent of
 * whether the install modal ever renders.
 *
 * `appinstalled` also fires on iOS/desktop-never, so this store is the one
 * place that marks the device `done` on a real install regardless of which
 * UI (our modal, or the browser's own affordance) triggered it.
 */

let deferredEvent = null;
const listeners = new Set();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function handleBeforeInstallPrompt(event) {
  event.preventDefault?.();
  deferredEvent = event;
  emit();
}

function handleAppInstalled() {
  deferredEvent = null;
  try {
    markInstallPromptDone();
  } catch {
    // non-fatal — the install itself already succeeded
  }
  emit();
}

/**
 * Attaches the two listeners. Exported (not just run at import time) so
 * tests can call it against a fake `win` double; the module also calls it
 * once against the real `window` below, guarded for non-browser environments
 * (SSR, Jest without jsdom globals wired yet).
 */
export function initInstallPromptCapture(win) {
  if (!win || typeof win.addEventListener !== 'function') return () => {};
  win.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  win.addEventListener('appinstalled', handleAppInstalled);
  return () => {
    win.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    win.removeEventListener('appinstalled', handleAppInstalled);
  };
}

/** Is a captured event available to prompt with right now? */
export function canPromptInstall() {
  return deferredEvent != null;
}

/**
 * Fires the captured native prompt. Resolves 'accepted' | 'dismissed' |
 * 'unavailable' (no event captured — caller should fall back to manual
 * steps). A single event can only be consumed once, so it's cleared
 * immediately regardless of outcome.
 */
export async function promptInstall() {
  const event = deferredEvent;
  if (!event) return 'unavailable';
  deferredEvent = null;
  emit();
  try {
    event.prompt?.();
    const choice = await event.userChoice;
    if (choice?.outcome === 'accepted') {
      markInstallPromptDone();
      return 'accepted';
    }
    return 'dismissed';
  } catch {
    return 'dismissed';
  }
}

/** React binding for `canPromptInstall()` — reactive to a LATE-arriving event. */
export function useCanPromptInstall() {
  return useSyncExternalStore(subscribe, canPromptInstall, () => false);
}

/** Tests only. */
export function resetInstallPromptStoreForTests() {
  deferredEvent = null;
}

if (typeof window !== 'undefined') {
  initInstallPromptCapture(window);
}
